import React, { useState, useEffect } from "react";
import { FormGroup, FormLabel, Form, Col, Row, Button, Modal, FormControl, FormSelect, Offcanvas } from "react-bootstrap";
import { InputGroup } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import PhysicalImage1 from "../../../images/product-img.png";
import EditIcon from "../../../images/edit-icon.svg";
import DeleteIcon from "../../../images/delete-icon.svg";
import SearchIcon from "../../../images/search-icon.svg";
import NoteIcon from "../../../images/note-icon.svg";
import FileIcon from "../../../images/delete-file.svg";
import FileUploader from "../FileUploader/FileUpload.tsx";
import { giftItemApi } from "./Api.tsx";
import { toast } from "react-toastify";

interface PhysicalItemType {
    id: number;
    _id?: string;
    image: string;
    title: string;
    description: string;
    category: string;
    price: string;
    note?: string;
}

interface PhysicalItemProps {
    onEdit: (item: PhysicalItemType) => void;
    onDelete: (id: number) => void;
}

const PhysicalItem: React.FC<PhysicalItemProps> = ({ onEdit, onDelete }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showNotesOffcanvas, setShowNotesOffcanvas] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
    const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
    const [itemToDelete, setItemToDelete] = useState<PhysicalItemType | null>(null);
    const [itemForNotes, setItemForNotes] = useState<PhysicalItemType | null>(null);
    const [noteText, setNoteText] = useState('');
    
    const [formData, setFormData] = useState<PhysicalItemType>({
        id: 0,
        image: PhysicalImage1,
        title: '',
        description: '',
        category: '',
        price: '',
        note: ''
    });

    const [physicalItems, setPhysicalItems] = useState<PhysicalItemType[]>([]);
    const [allCategories, setAllCategories] = useState<string[]>([]);

    // Fetch physical items on component mount
    useEffect(() => {
        fetchPhysicalItems();
        fetchAllCategories();
    }, []);

    const fetchPhysicalItems = async () => {
        try {
            setLoading(true);
            const params: any = { type: 'PhysicalItem' };

            if (searchTerm.trim()) {
                params.search = searchTerm.trim();
            }

            if (selectedCategory) {
                params.category = selectedCategory;
            }

            const response = await giftItemApi.getAllGiftItems(params);
            if (response.status === 'Success') {
                // Convert API data to match original interface
                const apiItems = response.data?.map((item: any, index: number) => ({
                    id: index + 100, // Use high numbers to avoid conflicts
                    _id: item._id,
                    image: item.imageUrl || PhysicalImage1,
                    title: item.title,
                    description: item.description,
                    category: item.category,
                    price: `$${item.price}`,
                    note: item.note
                })) || [];

                setPhysicalItems(apiItems);
            }
        } catch (error) {
            console.error('Error fetching physical items:', error);
            toast.error('Failed to fetch physical items');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllCategories = async () => {
        try {
            // Fetch all items without any filters to get all categories
            const response = await giftItemApi.getAllGiftItems({ type: 'PhysicalItem' });
            if (response.status === 'Success') {
                // Extract unique categories from all items
                const categories = Array.from(
                    new Set(
                        response.data
                            ?.map((item: any) => item.category)
                            .filter((category: string) => category && category.trim() !== '')
                    )
                ).sort();

                setAllCategories(categories as string[]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Refetch when search term or category changes
    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchTerm.length > 2 || searchTerm.length === 0) {
                fetchPhysicalItems();
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm, selectedCategory]);

    const truncate = (str: string, maxLength: number) => {
        return str.length > maxLength ? str.slice(0, maxLength) + "..." : str;
    };

    const openAddModal = () => {
        setFormData({ id: 0, image: PhysicalImage1, title: '', description: '', category: '', price: '', note: '' });
        setUploadedImageUrl("");
        setSelectedImageFile(null);
        setIsEditing(false);
        setShowModal(true);
    };

    const openEditModal = (item: PhysicalItemType) => {
        setFormData(item);
        setUploadedImageUrl(item.image);
        setSelectedImageFile(null);
        setIsEditing(true);
        setShowModal(true);
    };

    // Delete Modal Handlers
    const handleDeleteShow = (item: PhysicalItemType) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const handleDeleteClose = () => {
        setShowDeleteModal(false);
        setItemToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        try {
            setLoading(true);
            
            if (itemToDelete._id) {
                // Delete via API
                const response = await giftItemApi.softDeleteGiftItem(itemToDelete._id);
                
                if (response.status === 'Success') {
                    toast.success(response.message || 'Physical item deleted successfully');
                    await fetchPhysicalItems();
                    handleDeleteClose();
                } else {
                    toast.error(response.message || 'Failed to delete physical item');
                }
            } else {
                // Fallback for local items (original behavior)
                setPhysicalItems(prev => prev.filter(i => i.id !== itemToDelete.id));
                toast.success('Physical item deleted successfully');
                onDelete(itemToDelete.id); // Call original onDelete prop
                handleDeleteClose();
            }
        } catch (error: any) {
            console.error('Error deleting physical item:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to delete physical item');
        } finally {
            setLoading(false);
        }
    };

    // Notes Offcanvas Handlers
    const handleNotesShow = (item: PhysicalItemType) => {
        setItemForNotes(item);
        setNoteText(item.note || '');
        setShowNotesOffcanvas(true);
    };

    const handleNotesClose = () => {
        setShowNotesOffcanvas(false);
        setItemForNotes(null);
        setNoteText('');
    };

    const handleNotesSave = async () => {
        if (!itemForNotes || !itemForNotes._id) {
            toast.error('Cannot update notes without item ID');
            return;
        }

        // Check if notes have changed
        const originalNote = itemForNotes.note || '';
        const newNote = noteText.trim();

        if (originalNote === newNote) {
            toast.info('No changes to save');
            return;
        }

        try {
            setLoading(true);

            const formData = new FormData();
            formData.append('note', newNote);

            const response = await giftItemApi.updateGiftItem(itemForNotes._id, formData);

            if (response.status === 'Success') {
                toast.success(newNote ? 'Notes updated successfully' : 'Notes removed successfully');
                await fetchPhysicalItems();
                handleNotesClose();
            } else {
                toast.error(response.message || 'Failed to update notes');
            }
        } catch (error: any) {
            console.error('Error updating notes:', error);
            toast.error(error.response?.data?.message || 'Failed to update notes');
        } finally {
            setLoading(false);
        }
    };

    // Handle file selection from FileUploader component
    const handleFileSelect = (file: File | null) => {
        setSelectedImageFile(file);
        if (file) {
            // Create a preview URL
            const previewUrl = URL.createObjectURL(file);
            setUploadedImageUrl(previewUrl);
        } else {
            setUploadedImageUrl("");
        }
    };

    const handleSave = async () => {
        // Validation
        if (!formData.title.trim()) {
            toast.error('Title is required');
            return;
        }
        if (!formData.description.trim()) {
            toast.error('Description is required');
            return;
        }
        if (!formData.category.trim()) {
            toast.error('Category is required');
            return;
        }
        if (!formData.price || (typeof formData.price === 'string' && !formData.price.replace('$', '').trim())) {
            toast.error('Price is required');
            return;
        }

        // Check for image requirement
        if (!isEditing && !selectedImageFile) {
            toast.error('Image is required for new items');
            return;
        }

        try {
            setLoading(true);
            
            if (isEditing && formData._id) {
                // Update existing item via API
                const submitFormData = new FormData();
                submitFormData.append('title', formData.title.trim());
                submitFormData.append('description', formData.description.trim());
                submitFormData.append('category', formData.category.trim());
                submitFormData.append('price', formData.price.toString().replace('$', ''));
                
                if (formData.note?.trim()) {
                    submitFormData.append('note', formData.note.trim());
                }

                // Add image file if a new one was selected
                if (selectedImageFile) {
                    submitFormData.append('image', selectedImageFile);
                }

                const response = await giftItemApi.updateGiftItem(formData._id, submitFormData);
                if (response.status === 'Success') {
                    toast.success(response.message || 'Physical item updated successfully');
                    await fetchPhysicalItems();
                    setShowModal(false);
                } else {
                    toast.error(response.message || 'Failed to update physical item');
                }
            } else if (!isEditing) {
                // Create new item via API
                const submitFormData = new FormData();
                submitFormData.append('type', 'PhysicalItem');
                submitFormData.append('title', formData.title.trim());
                submitFormData.append('description', formData.description.trim());
                submitFormData.append('category', formData.category.trim());
                submitFormData.append('price', formData.price.toString().replace('$', ''));
                
                if (formData.note?.trim()) {
                    submitFormData.append('note', formData.note.trim());
                }

                // Add image file (required for new items)
                if (selectedImageFile) {
                    submitFormData.append('image', selectedImageFile);
                }

                const response = await giftItemApi.createGiftItem(submitFormData);
                if (response.status === 'Success') {
                    toast.success(response.message || 'Physical item created successfully');
                    await fetchPhysicalItems();
                    setShowModal(false);
                } else {
                    toast.error(response.message || 'Failed to create physical item');
                }
            }
        } catch (error: any) {
            console.error('Error saving physical item:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to save physical item');
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setFormData({ id: 0, image: PhysicalImage1, title: '', description: '', category: '', price: '', note: '' });
        setUploadedImageUrl("");
        setSelectedImageFile(null);
        setIsEditing(false);
    };

    return (
        <div className="physical-items-table">
            <div className="main-table physical-strap-block mb-4">
                <Row className="justify-content-between main-table-filter" style={{ alignItems: "center" }}>
                    <Col md={6} lg={5} xl={3} className='select-field table-select'>
                        <FormGroup className='form-group w-100 px-1'>
                            <FormLabel>Filter By</FormLabel>
                            <Form.Select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                disabled={loading}
                            >
                                <option value="">All Categories</option>
                                {allCategories.map((category, index) => (
                                    <option key={index} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </Form.Select>
                        </FormGroup>
                    </Col>
                    <Col md={6} lg={6} xl={4} className="physical-strap-block-right">
                        <FormGroup className='form-group w-100 px-1 search-box'>
                            <InputGroup>
                                <FormControl
                                    type="text"
                                    placeholder="Search"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={loading}
                                />
                                <InputGroup.Text>
                                    <img src={SearchIcon} alt='' />
                                </InputGroup.Text>
                            </InputGroup>
                        </FormGroup>
                        <Button className="main-bttn" onClick={openAddModal} disabled={loading}>
                            Add New
                        </Button>
                    </Col>
                </Row>
            </div>
            <div className="table-responsive">
                <Table hover className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Item</th>
                            <th>Title</th>
                            <th>Description</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th className="actions">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && physicalItems.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center">
                                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px' }}>
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="ms-2">Loading physical items...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : physicalItems.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center">
                                    No physical items found
                                </td>
                            </tr>
                        ) : (
                            physicalItems.map((item) => (
                                <tr key={item.id}>
                                    <td>
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            style={{ width: '70px', height: '50px', objectFit: 'cover', borderRadius: '8px' }}
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = PhysicalImage1;
                                            }}
                                        />
                                    </td>
                                    <td>{item.title}</td>
                                    <td>{truncate(item.description, 70)}</td>
                                    <td>{item.category}</td>
                                    <td>{item.price}</td>
                                    <td>
                                        <div className="d-flex gap-2 table-options">
                                            <img
                                                src={EditIcon}
                                                alt="Edit"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => openEditModal(item)}
                                            />
                                            <img
                                                src={NoteIcon}
                                                alt="Note"
                                                style={{ cursor: 'pointer' }}
                                                title={item.note || 'Add/Edit Notes'}
                                                onClick={() => handleNotesShow(item)}
                                            />
                                            <img
                                                src={DeleteIcon}
                                                alt="Delete"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleDeleteShow(item)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>

            {/* Modal for Add/Edit */}
            <Modal className="main-modal" show={showModal} onHide={handleModalClose} centered>
                <Modal.Header closeButton className="px-0">
                    <Modal.Title>{isEditing ? "Edit Physical Item" : "Add New Physical Item"}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-0 mt-2">
                    <FormGroup className="form-group">
                        <FormLabel>Title *</FormLabel>
                        <FormControl
                            type="text"
                            placeholder="Enter Item Title"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            disabled={loading}
                            maxLength={100}
                        />
                    </FormGroup>

                    <FormGroup className="form-group">
                        <FormLabel>Category *</FormLabel>
                        <FormControl
                            type="text"
                            placeholder="Enter Category"
                            value={formData.category}
                            onChange={(e) => setFormData({...formData, category: e.target.value})}
                            disabled={loading}
                            maxLength={50}
                        />
                    </FormGroup>

                    <FormGroup className="form-group">
                        <FormLabel>Price ($) *</FormLabel>
                        <FormControl
                            type="number"
                            placeholder="Enter Price"
                            value={formData.price.toString().replace('$', '')}
                            onChange={(e) => setFormData({...formData, price: e.target.value})}
                            disabled={loading}
                            min="0"
                            step="0.01"
                        />
                    </FormGroup>

                    <FormGroup className="form-group">
                        <FormLabel>Description *</FormLabel>
                        <FormControl
                            as="textarea"
                            rows={3}
                            placeholder="Enter Description"
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            disabled={loading}
                            maxLength={500}
                        />
                    </FormGroup>

                    <FormGroup className="form-group">
                        <FormLabel>Notes</FormLabel>
                        <FormControl
                            as="textarea"
                            rows={2}
                            placeholder="Additional Notes"
                            value={formData.note || ''}
                            onChange={(e) => setFormData({...formData, note: e.target.value})}
                            disabled={loading}
                            maxLength={250}
                        />
                    </FormGroup>

                    <FileUploader 
                        onFileSelect={handleFileSelect}
                        initialImageUrl={isEditing ? formData.image : undefined}
                    />
                </Modal.Body>
                <Modal.Footer className="px-0 border-0">
                    <Button
                        className="main-bttn"
                        variant="primary"
                        onClick={handleSave}
                        disabled={!formData.title.trim() || !formData.description.trim() || !formData.category.trim() || !formData.price || (!isEditing && !selectedImageFile) || loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {isEditing ? 'Updating...' : 'Adding...'}
                            </>
                        ) : (
                            isEditing ? 'Update' : 'Add'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={showDeleteModal}
                onHide={handleDeleteClose}
                centered
                className="delete-modal"
            >
                <Modal.Header closeButton style={{ border: 'none' }}>
                    <Modal.Title style={{ textTransform: 'capitalize' }}>DELETE CONFIRMATION</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <div className="mb-3">
                            <img src={FileIcon} alt="" />
                        </div>
                        <div>
                            <p className='mw-100' style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                Are you sure you want to delete "{itemToDelete?.title}"?
                            </p>
                            <p className='mw-100'>This action cannot be undone.</p>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 text-left gap-3">
                    <Button
                        className="border-bttn"
                        variant="secondary"
                        onClick={handleDeleteClose}
                        disabled={loading}
                        style={{ width: "30%", margin: "0" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        className="main-bttn"
                        variant="danger"
                        onClick={handleDeleteConfirm}
                        disabled={loading}
                        style={{ width: "30%", margin: "0" }}
                    >
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .delete-modal .btn-close {
                    filter: brightness(0) invert(1);
                }
            `}</style>

            {/* Notes Offcanvas */}
            <Offcanvas
                show={showNotesOffcanvas}
                onHide={handleNotesClose}
                placement="end"
                className="notes-offcanvas"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Add/Edit Notes</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <FormGroup className="form-group">
                        <FormLabel>Title</FormLabel>
                        <FormControl
                            type="text"
                            value={itemForNotes?.title || ''}
                            disabled
                        />
                    </FormGroup>

                    <FormGroup className="form-group">
                        <FormLabel>Notes</FormLabel>
                        <FormControl
                            as="textarea"
                            rows={6}
                            placeholder="Add notes here..."
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            disabled={loading}
                            maxLength={500}
                        />
                        <small className="text-muted">{noteText.length}/500 characters</small>
                    </FormGroup>

                    <div className="d-flex gap-2 mt-3">
                        <Button
                            className="main-bttn"
                            onClick={handleNotesSave}
                            disabled={loading || (itemForNotes?.note || '') === noteText.trim()}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Saving...
                                </>
                            ) : (
                                'Save Notes'
                            )}
                        </Button>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </div>
    );
};

export default PhysicalItem;